import { motion } from 'framer-motion';
import { useAudio } from '../context/AudioContext';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Volume2, VolumeX, Music, Music2 } from 'lucide-react';
import { useState } from 'react';

// Floating Audio Controls
export const AudioControls = () => {
  const { 
    settings, 
    isPlaying, 
    toggleMusic, 
    toggleSfx, 
    setMusicVolume, 
    setSfxVolume 
  } = useAudio();
  const [expanded, setExpanded] = useState(false);
  
  return (
    <motion.div 
      className="fixed bottom-4 left-4 z-40"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 1 }}
    >
      {/* Collapsed Button */}
      <motion.button
        onClick={() => setExpanded(!expanded)}
        className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
          settings.musicEnabled || settings.sfxEnabled 
            ? 'bg-babylon-gold/20 border border-babylon-gold/40' 
            : 'bg-babylon-obsidian/80 border border-babylon-gold/20'
        }`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        {settings.musicEnabled ? (
          <Music className="w-4 h-4 text-babylon-gold" />
        ) : (
          <VolumeX className="w-4 h-4 text-babylon-text-dim" />
        )}
      </motion.button>
      
      {/* Expanded Panel */}
      {expanded && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-12 left-0 w-56 bg-babylon-abyss border border-babylon-gold/30 rounded-lg p-3 shadow-xl"
        >
          <h4 className="text-xs font-medium text-babylon-gold mb-3 flex items-center gap-2">
            <Music2 size={14} /> Audio Settings
          </h4>
          
          {/* Music Toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-babylon-text">Background Music</span>
              <button
                onClick={toggleMusic}
                className={`w-10 h-5 rounded-full transition-all ${
                  settings.musicEnabled ? 'bg-babylon-gold' : 'bg-babylon-obsidian'
                }`}
              >
                <motion.div
                  className="w-4 h-4 rounded-full bg-white shadow"
                  animate={{ x: settings.musicEnabled ? 20 : 2 }}
                />
              </button>
            </div>
            
            {settings.musicEnabled && (
              <div className="flex items-center gap-2">
                <Volume2 size={12} className="text-babylon-text-dim" />
                <Slider
                  value={[settings.musicVolume * 100]}
                  onValueChange={(v) => setMusicVolume(v[0] / 100)}
                  max={100}
                  step={1}
                  className="flex-1"
                />
                <span className="text-[10px] text-babylon-text-dim w-8">{Math.round(settings.musicVolume * 100)}%</span>
              </div>
            )}
            
            {/* SFX Toggle */}
            <div className="flex items-center justify-between pt-2 border-t border-babylon-gold/10">
              <span className="text-xs text-babylon-text">Sound Effects</span>
              <button
                onClick={toggleSfx}
                className={`w-10 h-5 rounded-full transition-all ${
                  settings.sfxEnabled ? 'bg-babylon-gold' : 'bg-babylon-obsidian'
                }`}
              >
                <motion.div
                  className="w-4 h-4 rounded-full bg-white shadow"
                  animate={{ x: settings.sfxEnabled ? 20 : 2 }}
                />
              </button>
            </div>
            
            {settings.sfxEnabled && (
              <div className="flex items-center gap-2">
                <Volume2 size={12} className="text-babylon-text-dim" />
                <Slider
                  value={[settings.sfxVolume * 100]}
                  onValueChange={(v) => setSfxVolume(v[0] / 100)}
                  max={100}
                  step={1}
                  className="flex-1"
                />
                <span className="text-[10px] text-babylon-text-dim w-8">{Math.round(settings.sfxVolume * 100)}%</span>
              </div>
            )}
          </div>
          
          <p className="text-[10px] text-babylon-text-dim mt-3 italic">
            Click anywhere to enable audio
          </p>
        </motion.div>
      )}
    </motion.div>
  );
};

// T3B3TU Website Link Component
export const T3B3TULink = () => {
  return (
    <motion.a
      href="https://www.t3b3tu.com"
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-4 left-16 z-40 text-[10px] text-babylon-gold/60 hover:text-babylon-gold transition-colors font-mono tracking-wider flex items-center gap-1"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.5 }}
      whileHover={{ scale: 1.05 }}
    >
      <span className="text-babylon-gold/40">☉</span>
      t3b3tu.com
    </motion.a>
  );
};

export default AudioControls;
